import {
  eventTypes,
  actionNames,
  type ActionNameValue,
  type ActionPrefixValue,
  type DimensionPropertyNames,
  type EventUnshapedData,
  type ObjectTypeValue,
} from "@newsela/snitch";

type TrackAction = (dataObj: EventUnshapedData) => void;

type GetEventName = (
  objectType?: ObjectTypeValue | null,
  actionPrefix?: ActionPrefixValue | null,
  actionName?: ActionNameValue | null,
) => string;

export const PAGE_VIEW_DESCRIPTION = "Page view.";
export const PAGE_VIEW_AFTER_LEVEL_DESCRIPTION =
  "Page view after article level set.";

type PageViewDimensionProps = Omit<DimensionPropertyNames, "content_id"> & {
  article_story_content_id?: string;
  article_level_content_id?: string | null;
  content_id?: string | null;
  view_type?: string;
};

/**
 * Fires the standard Page View event via the snitch tracker.
 *
 * Encapsulates the Page View event shape so it is defined once and shared
 * between the default page-level tracking (`useTrackPageView`) and any
 * route that fires its own enriched Page View (e.g. the bundle route).
 *
 * @param trackAction - the snitch `trackAction` function
 * @param getEventName - the snitch `getEventName` function
 * @param extraDimensionProps - additional `dimension_properties` to merge
 *   into the event (e.g. `content_id`, `content_type`)
 * @param extraTopLevelProps - additional top-level event properties to
 *   merge (e.g. `screen_size`, which is not part of `dimension_properties`)
 */
export const trackPageView = (
  trackAction: TrackAction,
  getEventName: GetEventName,
  extraDimensionProps?: PageViewDimensionProps,
  extraTopLevelProps?: Record<string, unknown>,
) => {
  trackAction({
    event_type: eventTypes.pageView,
    event_name: getEventName(null, null, actionNames.pageView),
    // Cast because the widened properties above are valid for ser_davos but
    // are not yet part of snitch's `DimensionPropertyNames`.
    dimension_properties: {
      description: PAGE_VIEW_DESCRIPTION,
      ...extraDimensionProps,
    } as DimensionPropertyNames,
    ...extraTopLevelProps,
  });
};

export default trackPageView;
